import { useEffect, useState } from 'react';
import { AppShell } from '@/components/AppShell';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Link } from '@tanstack/react-router';
import { ArrowRight } from 'lucide-react';
import { useStopSelection, useStopSelectionActions } from './state';
import { useSchedule, useScheduleActions } from '../schedule/state';

export function StopPickerPage() {
  const snapshot = useStopSelection((current) => current);
  const actions = useStopSelectionActions();
  const scheduleSnapshot = useSchedule((current) => current);
  const scheduleActions = useScheduleActions();
  const [selectedScheduleId, setSelectedScheduleId] = useState('');
  const view = snapshot.context.view;
  const schedules = scheduleSnapshot.context.items;
  const scheduleId = selectedScheduleId;
  const stopSelectionId = snapshot.context.stopSelectionId ?? view?.stopSelectionId ?? '';
  const selectedRouteId = view?.routeId ?? '';
  const selectedDirectionId = view?.directionId ?? '';
  const selectedStopId = view?.stopId ?? '';

  useEffect(() => {
    if (snapshot.matches('idle')) actions.loadView();
  }, [actions, snapshot]);
  useEffect(() => {
    if (scheduleSnapshot.matches('idle')) scheduleActions.loadBrowse();
  }, [scheduleActions, scheduleSnapshot]);
  useEffect(() => {
    if (!selectedScheduleId && schedules[0]?.scheduleId) setSelectedScheduleId(schedules[0].scheduleId);
  }, [schedules, selectedScheduleId]);

  const routes = (view?.routes ?? []).filter((item): item is NonNullable<typeof item> =>
    Boolean(item?.routeId && item.routeName),
  );
  const directions = (view?.directions ?? []).filter((item): item is NonNullable<typeof item> =>
    Boolean(item?.directionId && item.directionName && item.routeId === selectedRouteId),
  );
  const stops = (view?.stops ?? []).filter((item): item is NonNullable<typeof item> =>
    Boolean(item?.stopId && item.stopName && item.directionId === selectedDirectionId),
  );
  const busy =
    snapshot.matches('loading') ||
    snapshot.matches('choosingRoute') ||
    snapshot.matches('choosingDirection') ||
    snapshot.matches('choosingStop');

  return (
    <AppShell
      title="Choose a stop"
      description="Set your route, direction, and stop to build a dependable departure board."
    >
      {snapshot.matches('error') ? (
        <div className="rounded-[14px] border border-destructive/30 bg-card p-8">
          <h2 className="font-heading text-xl font-semibold">We couldn’t load the stop choices.</h2>
          <p className="mt-2 text-muted-foreground">Try again to fetch the latest route and stop information.</p>
          <Button className="mt-5" onClick={() => actions.loadView()}>
            Try again
          </Button>
        </div>
      ) : (
        <div className="max-w-xl rounded-[14px] border border-border bg-card p-6 sm:p-8">
          <div className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="schedule">Service schedule</Label>
              <Select
                value={selectedScheduleId || null}
                items={schedules.map((item) => ({ value: item.scheduleId, label: item.scheduleId }))}
                onValueChange={(value) => {
                  if (typeof value === 'string') setSelectedScheduleId(value);
                }}
                disabled={busy || scheduleSnapshot.matches('loading') || !schedules.length}
              >
                <SelectTrigger id="schedule" className="h-11 w-full" data-jr-key="choose-route-scheduleId">
                  <SelectValue placeholder="Select a schedule" />
                </SelectTrigger>
                <SelectContent>
                  {schedules.map((item, index) => (
                    <SelectItem
                      key={item.scheduleId}
                      value={item.scheduleId}
                      data-jr-key={index === 0 ? 'choose-route-schedule' : undefined}
                    >
                      {item.scheduleId}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="route">Route</Label>
              <Select
                value={selectedRouteId || null}
                items={routes.map((item) => ({ value: item.routeId!, label: item.routeName! }))}
                onValueChange={(value) => {
                  if (typeof value === 'string' && scheduleId) actions.chooseRoute(scheduleId, value);
                }}
                disabled={busy || !routes.length}
              >
                <SelectTrigger id="route" className="h-11 w-full" data-jr-key="choose-route-routeId">
                  <SelectValue placeholder="Select a route" />
                </SelectTrigger>
                <SelectContent>
                  {routes.map((item) => (
                    <SelectItem
                      key={item.routeId}
                      value={item.routeId!}
                      data-jr-key={routes.indexOf(item) === 0 ? 'choose-route' : undefined}
                    >
                      {item.routeName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="direction">Direction</Label>
              <Select
                value={selectedDirectionId || null}
                items={directions.map((item) => ({ value: item.directionId!, label: item.directionName! }))}
                onValueChange={(value) => {
                  if (typeof value === 'string' && stopSelectionId) actions.chooseDirection(stopSelectionId, value);
                }}
                disabled={busy || !stopSelectionId || !directions.length}
              >
                <SelectTrigger id="direction" className="h-11 w-full" data-jr-key="choose-direction-directionId">
                  <SelectValue placeholder="Select a direction" />
                </SelectTrigger>
                <SelectContent>
                  {directions.map((item) => (
                    <SelectItem
                      key={item.directionId}
                      value={item.directionId!}
                      data-jr-key={directions.indexOf(item) === 0 ? 'choose-direction' : undefined}
                    >
                      {item.directionName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="stop">Stop</Label>
              <Select
                value={selectedStopId || null}
                items={stops.map((item) => ({ value: item.stopId!, label: item.stopName! }))}
                onValueChange={(value) => {
                  if (typeof value === 'string' && stopSelectionId) actions.chooseStop(stopSelectionId, value);
                }}
                disabled={busy || !stopSelectionId || !selectedDirectionId || !stops.length}
              >
                <SelectTrigger id="stop" className="h-11 w-full" data-jr-key="choose-stop-stopId">
                  <SelectValue placeholder="Select a stop" />
                </SelectTrigger>
                <SelectContent>
                  {stops.map((item) => (
                    <SelectItem
                      key={item.stopId}
                      value={item.stopId!}
                      data-jr-key={stops.indexOf(item) === 0 ? 'choose-stop' : undefined}
                    >
                      {item.stopName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          {selectedStopId ? (
            <Button render={<Link to="/" />} className="mt-8" data-jr-key="view-bus-times">
              View bus times <ArrowRight className="size-4" />
            </Button>
          ) : (
            <p className="mt-8 text-sm text-muted-foreground">Choose a stop to see its scheduled departures.</p>
          )}
        </div>
      )}
    </AppShell>
  );
}
